import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Loader2, Save, Plus, Trash2, Pencil, X, FileText, Printer, Send, ArrowLeft, Building2, BarChart3, Download,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["Draft", "Sent", "Paid", "Overdue"];
const today = () => new Date().toISOString().slice(0, 10);
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const statusStyle = (s) => ({
  Draft: "bg-muted text-foreground/60",
  Sent: "bg-secondary/20 text-primary",
  Paid: "bg-green-100 text-green-700",
  Overdue: "bg-red-100 text-red-600",
}[s] || "bg-muted text-foreground/60");

const emptyInvoice = () => ({
  number: "",
  client_name: "",
  client_company: "",
  client_email: "",
  client_address: "",
  client_gstin: "",
  issue_date: today(),
  due_date: "",
  items: [{ description: "", quantity: 1, rate: 0 }],
  discount_type: "flat",
  discount_value: 0,
  tax_rate: 18,
  notes: "",
  terms: "Payment due within 14 days.",
  status: "Draft",
});

const calcTotals = (inv) => {
  const subtotal = (inv.items || []).reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.rate) || 0), 0);
  let discount = inv.discount_type === "percent" ? subtotal * (Number(inv.discount_value) || 0) / 100 : (Number(inv.discount_value) || 0);
  discount = Math.max(0, Math.min(discount, subtotal));
  const taxable = subtotal - discount;
  const tax = taxable * (Number(inv.tax_rate) || 0) / 100;
  return { subtotal, discount, tax, total: taxable + tax };
};

const buildPrintHTML = (inv, biller) => {
  const t = calcTotals(inv);
  const rows = (inv.items || []).map((i) => `
    <tr>
      <td>${i.description || ""}</td>
      <td class="r">${Number(i.quantity) || 0}</td>
      <td class="r">${inr(i.rate)}</td>
      <td class="r">${inr((Number(i.quantity) || 0) * (Number(i.rate) || 0))}</td>
    </tr>`).join("");
  return `<!doctype html><html><head><meta charset="utf-8"/><title>Invoice ${inv.number || ""}</title>
  <style>
    *{box-sizing:border-box;} body{font-family:Arial,Helvetica,sans-serif;color:#12281f;margin:0;padding:40px;background:#fff;}
    .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #0e9083;padding-bottom:20px;margin-bottom:28px;}
    .co{font-size:22px;font-weight:800;color:#0e9083;} .muted{color:#6b7a76;font-size:13px;line-height:1.5;}
    h1{font-size:26px;margin:0 0 4px;letter-spacing:1px;} .meta{text-align:right;font-size:13px;}
    .grid{display:flex;justify-content:space-between;gap:24px;margin-bottom:24px;}
    .box{font-size:13px;line-height:1.6;} .label{text-transform:uppercase;letter-spacing:1px;font-size:11px;color:#9aa;margin-bottom:6px;}
    table{width:100%;border-collapse:collapse;margin-top:8px;font-size:14px;}
    th{text-align:left;background:#f0f7f5;padding:10px;font-size:12px;color:#4a5a56;} td{padding:10px;border-bottom:1px solid #eee;} .r{text-align:right;}
    .totals{width:280px;margin-left:auto;margin-top:16px;font-size:14px;} .totals tr td{border:0;padding:6px 0;} .totals .r{text-align:right;}
    .total-row td{border-top:2px solid #0e9083;font-weight:800;font-size:16px;padding-top:10px;}
    .status{display:inline-block;padding:4px 12px;border-radius:20px;font-size:12px;font-weight:700;background:#e3f2ef;color:#0e9083;}
    .foot{margin-top:36px;font-size:12px;color:#6b7a76;border-top:1px solid #eee;padding-top:16px;line-height:1.6;}
    @media print{ body{padding:24px;} }
  </style></head><body onload="window.print()">
    <div class="head">
      <div>
        <div class="co">${biller.company_name || ""}</div>
        <div class="muted">${(biller.address || "").replace(/\n/g, "<br/>")}${biller.gstin ? "<br/>GSTIN: " + biller.gstin : ""}${biller.email ? "<br/>" + biller.email : ""}${biller.phone ? " · " + biller.phone : ""}</div>
      </div>
      <div class="meta">
        <h1>INVOICE</h1>
        <div class="muted"># ${inv.number || "(draft)"}</div>
        <div class="muted">Issued: ${inv.issue_date || "—"}</div>
        <div class="muted">Due: ${inv.due_date || "—"}</div>
        <div style="margin-top:8px;"><span class="status">${inv.status || "Draft"}</span></div>
      </div>
    </div>
    <div class="grid">
      <div class="box"><div class="label">Bill to</div>
        <b>${inv.client_name || ""}</b><br/>${inv.client_company || ""}<br/>
        ${(inv.client_address || "").replace(/\n/g, "<br/>")}${inv.client_gstin ? "<br/>GSTIN: " + inv.client_gstin : ""}${inv.client_email ? "<br/>" + inv.client_email : ""}
      </div>
    </div>
    <table><thead><tr><th>Description</th><th class="r">Qty</th><th class="r">Rate</th><th class="r">Amount</th></tr></thead>
      <tbody>${rows}</tbody></table>
    <table class="totals">
      <tr><td class="muted">Subtotal</td><td class="r">${inr(t.subtotal)}</td></tr>
      <tr><td class="muted">Discount${inv.discount_type === "percent" ? " (" + (inv.discount_value || 0) + "%)" : ""}</td><td class="r">−${inr(t.discount)}</td></tr>
      <tr><td class="muted">GST (${inv.tax_rate || 0}%)</td><td class="r">${inr(t.tax)}</td></tr>
      <tr class="total-row"><td>Total</td><td class="r">${inr(t.total)}</td></tr>
    </table>
    ${inv.notes ? `<div class="foot"><b>Notes:</b> ${inv.notes}</div>` : ""}
    ${inv.terms ? `<div class="foot"><b>Terms:</b> ${inv.terms}</div>` : ""}
  </body></html>`;
};

export const InvoicesTab = ({ auth }) => {
  const [invoices, setInvoices] = useState([]);
  const [biller, setBiller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list | edit | billing
  const [form, setForm] = useState(emptyInvoice());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [reportFilters, setReportFilters] = useState({ start: "", end: "", status: "all" });
  const [reportData, setReportData] = useState(null);
  const [genReport, setGenReport] = useState(false);

  const load = useCallback(async () => {
    try {
      const [inv, bp] = await Promise.all([
        axios.get(`${API}/admin/invoices`, auth()),
        axios.get(`${API}/admin/billing-profile`, auth()),
      ]);
      setInvoices(inv.data);
      setBiller(bp.data);
    } catch (e) {
      toast.error("Couldn't load invoices.");
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (idx, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [k]: v } : it) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { description: "", quantity: 1, rate: 0 }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const startNew = () => { setForm(emptyInvoice()); setEditingId(null); setView("edit"); };
  const startEdit = (inv) => { setForm({ ...emptyInvoice(), ...inv }); setEditingId(inv.id); setView("edit"); };

  const save = async () => {
    if (!form.client_name) return toast.error("Client name is required.");
    setSaving(true);
    try {
      if (editingId) await axios.put(`${API}/admin/invoices/${editingId}`, form, auth());
      else await axios.post(`${API}/admin/invoices`, form, auth());
      toast.success(editingId ? "Invoice updated." : "Invoice created.");
      setView("list");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save invoice.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.number}?`)) return;
    await axios.delete(`${API}/admin/invoices/${inv.id}`, auth());
    toast.success("Invoice deleted.");
    await load();
  };

  const changeStatus = async (inv, status) => {
    setInvoices((prev) => prev.map((r) => r.id === inv.id ? { ...r, status } : r));
    try { await axios.put(`${API}/admin/invoices/${inv.id}/status`, { status }, auth()); }
    catch { toast.error("Couldn't update status."); }
  };

  const printInvoice = (inv) => {
    const w = window.open("", "_blank");
    if (!w) return toast.error("Please allow pop-ups to print/download the PDF.");
    w.document.write(buildPrintHTML(inv, biller || {}));
    w.document.close();
  };

  const emailInvoice = async (inv) => {
    if (!inv.client_email) return toast.error("Add a client email first.");
    try {
      await axios.post(`${API}/admin/invoices/${inv.id}/send`, {}, auth());
      toast.success(`Invoice emailed to ${inv.client_email}.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send email."); }
  };

  const saveBiller = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/billing-profile`, biller, auth());
      toast.success("Billing profile saved.");
      setView("list");
    } catch { toast.error("Couldn't save billing profile."); }
    finally { setSaving(false); }
  };

  const generateReport = async () => {
    setGenReport(true);
    try {
      const params = new URLSearchParams(reportFilters).toString();
      const r = await axios.get(`${API}/admin/reports/invoices?${params}`, auth());
      setReportData(r.data);
    } catch { toast.error("Couldn't generate report."); }
    finally { setGenReport(false); }
  };

  const downloadReportCSV = () => {
    if (!reportData) return;
    const header = ["Number", "Client", "Company", "Issue date", "Due date", "Status", "Subtotal", "Discount", "GST", "Total"];
    const lines = reportData.invoices.map((i) =>
      [i.number, i.client_name, i.client_company, i.issue_date, i.due_date, i.status, i.subtotal, i.discount_amount, i.tax_amount, i.total]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `peoplelab-invoice-report-${today()}.csv`;
    link.click();
  };

  const printReport = () => {
    if (!reportData) return;
    const s = reportData.summary;
    const rows = reportData.invoices.map((i) => `
      <tr><td>${i.number}</td><td>${i.client_name || ""}</td><td>${i.issue_date || ""}</td>
      <td>${i.status}</td><td class="r">${inr(i.total)}</td></tr>`).join("");
    const range = `${reportFilters.start || "beginning"} → ${reportFilters.end || "today"}`;
    const w = window.open("", "_blank");
    if (!w) return toast.error("Please allow pop-ups to print the report.");
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"/><title>Invoice Report</title>
      <style>body{font-family:Arial,sans-serif;color:#12281f;padding:40px;} h1{color:#0e9083;margin:0;}
      .muted{color:#6b7a76;font-size:13px;} .cards{display:flex;gap:16px;margin:24px 0;flex-wrap:wrap;}
      .card{border:1px solid #e2ece9;border-radius:12px;padding:14px 18px;min-width:150px;} .card .v{font-size:20px;font-weight:800;}
      table{width:100%;border-collapse:collapse;font-size:14px;margin-top:12px;} th{background:#f0f7f5;text-align:left;padding:8px;} td{padding:8px;border-bottom:1px solid #eee;} .r{text-align:right;}
      </style></head><body onload="window.print()">
      <h1>${(biller && biller.company_name) || "Invoice"} — Report</h1>
      <div class="muted">Period: ${range} · Status: ${reportFilters.status}</div>
      <div class="cards">
        <div class="card"><div class="muted">Invoices</div><div class="v">${s.count}</div></div>
        <div class="card"><div class="muted">Total billed</div><div class="v">${inr(s.total)}</div></div>
        <div class="card"><div class="muted">Paid</div><div class="v">${inr(s.paid)}</div></div>
        <div class="card"><div class="muted">Outstanding</div><div class="v">${inr(s.outstanding)}</div></div>
        <div class="card"><div class="muted">GST collected</div><div class="v">${inr(s.tax)}</div></div>
      </div>
      <table><thead><tr><th>Number</th><th>Client</th><th>Issued</th><th>Status</th><th class="r">Total</th></tr></thead>
      <tbody>${rows}</tbody></table>
      </body></html>`);
    w.document.close();
  };

  if (loading) return <div className="py-20 text-center text-foreground/50"><Loader2 className="mx-auto animate-spin" size={26} /></div>;

  const inputCls = "w-full rounded-2xl border border-border bg-white px-4 py-2.5 text-sm outline-none focus:border-primary transition-colors";
  const t = calcTotals(form);
  const rows = statusFilter === "all" ? invoices : invoices.filter((i) => (i.status || "Draft") === statusFilter);

  // ---------- Billing profile editor ----------
  if (view === "billing" && biller) {
    return (
      <div data-testid="billing-profile">
        <button onClick={() => setView("list")} className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-primary mb-5"><ArrowLeft size={15} /> Back to invoices</button>
        <div className="flex items-center gap-3 mb-6">
          <span className="grid place-items-center h-11 w-11 rounded-2xl bg-primary/10 text-primary"><Building2 size={20} /></span>
          <div><h2 className="font-heading text-2xl font-medium">Billing profile</h2><p className="text-sm text-foreground/55">Your company details shown on every invoice.</p></div>
        </div>
        <div className="bg-card border border-border rounded-3xl p-6 max-w-2xl space-y-4">
          {[["company_name", "Company name"], ["gstin", "GSTIN"], ["email", "Billing email"], ["phone", "Phone"]].map(([k, l]) => (
            <div key={k}><label className="text-xs font-medium text-foreground/60 mb-2 block">{l}</label>
              <input data-testid={`biller-${k}`} className={inputCls} value={biller[k] || ""} onChange={(e) => setBiller({ ...biller, [k]: e.target.value })} /></div>
          ))}
          <div><label className="text-xs font-medium text-foreground/60 mb-2 block">Company address</label>
            <textarea data-testid="biller-address" rows={3} className={inputCls + " resize-none"} value={biller.address || ""} onChange={(e) => setBiller({ ...biller, address: e.target.value })} /></div>
          <button data-testid="save-biller" onClick={saveBiller} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-6 py-3 text-sm font-medium hover:bg-[#0d5f57] transition-colors disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save profile
          </button>
        </div>
      </div>
    );
  }

  // ---------- Invoice create/edit form ----------
  if (view === "edit") {
    return (
      <div data-testid="invoice-form">
        <button onClick={() => setView("list")} className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-primary mb-5"><ArrowLeft size={15} /> Back to invoices</button>
        <h2 className="font-heading text-2xl font-medium mb-6">{editingId ? "Edit invoice" : "New invoice"}</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-card border border-border rounded-3xl p-6">
              <div className="text-sm font-medium text-foreground/70 mb-4">Client details</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="inv-client-name" className={inputCls} placeholder="Client name *" value={form.client_name} onChange={(e) => set("client_name", e.target.value)} />
                <input data-testid="inv-client-company" className={inputCls} placeholder="Company" value={form.client_company} onChange={(e) => set("client_company", e.target.value)} />
                <input data-testid="inv-client-email" className={inputCls} placeholder="Client email" value={form.client_email} onChange={(e) => set("client_email", e.target.value)} />
                <input data-testid="inv-client-gstin" className={inputCls} placeholder="Client GSTIN" value={form.client_gstin} onChange={(e) => set("client_gstin", e.target.value)} />
                <textarea data-testid="inv-client-address" rows={2} className={inputCls + " sm:col-span-2 resize-none"} placeholder="Billing address" value={form.client_address} onChange={(e) => set("client_address", e.target.value)} />
              </div>
            </div>

            <div className="bg-card border border-border rounded-3xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm font-medium text-foreground/70">Line items</div>
                <button data-testid="inv-add-item" onClick={addItem} className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"><Plus size={15} /> Add item</button>
              </div>
              <div className="space-y-3">
                {form.items.map((it, idx) => (
                  <div key={idx} className="flex gap-2 items-start" data-testid={`inv-item-${idx}`}>
                    <input className={inputCls + " flex-1"} placeholder="Description" value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} data-testid={`inv-item-desc-${idx}`} />
                    <input type="number" className={inputCls + " w-20"} placeholder="Qty" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} data-testid={`inv-item-qty-${idx}`} />
                    <input type="number" className={inputCls + " w-28"} placeholder="Rate" value={it.rate} onChange={(e) => setItem(idx, "rate", e.target.value)} data-testid={`inv-item-rate-${idx}`} />
                    <div className="w-28 text-right text-sm py-2.5 text-foreground/70">{inr((Number(it.quantity) || 0) * (Number(it.rate) || 0))}</div>
                    <button onClick={() => removeItem(idx)} className="text-foreground/40 hover:text-red-500 py-2.5" data-testid={`inv-item-remove-${idx}`}><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-card border border-border rounded-3xl p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="text-xs font-medium text-foreground/60 mb-2 block">Notes</label>
                <textarea data-testid="inv-notes" rows={2} className={inputCls + " resize-none"} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
              <div><label className="text-xs font-medium text-foreground/60 mb-2 block">Terms</label>
                <textarea data-testid="inv-terms" rows={2} className={inputCls + " resize-none"} value={form.terms} onChange={(e) => set("terms", e.target.value)} /></div>
            </div>
          </div>

          {/* Sidebar: meta + totals */}
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-3xl p-6 space-y-4">
              <div><label className="text-xs font-medium text-foreground/60 mb-2 block">Invoice number</label>
                <input data-testid="inv-number" className={inputCls} placeholder="Auto-generated" value={form.number} onChange={(e) => set("number", e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-foreground/60 mb-2 block">Issue date</label>
                  <input type="date" data-testid="inv-issue-date" className={inputCls} value={form.issue_date} onChange={(e) => set("issue_date", e.target.value)} /></div>
                <div><label className="text-xs font-medium text-foreground/60 mb-2 block">Due date</label>
                  <input type="date" data-testid="inv-due-date" className={inputCls} value={form.due_date} onChange={(e) => set("due_date", e.target.value)} /></div>
              </div>
              <div><label className="text-xs font-medium text-foreground/60 mb-2 block">Status</label>
                <select data-testid="inv-status" className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select></div>
            </div>

            <div className="bg-card border border-border rounded-3xl p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-foreground/60 mb-2 block">Discount</label>
                  <input type="number" data-testid="inv-discount-value" className={inputCls} value={form.discount_value} onChange={(e) => set("discount_value", e.target.value)} /></div>
                <div><label className="text-xs font-medium text-foreground/60 mb-2 block">Type</label>
                  <select data-testid="inv-discount-type" className={inputCls} value={form.discount_type} onChange={(e) => set("discount_type", e.target.value)}>
                    <option value="flat">₹ Flat</option><option value="percent">% Percent</option>
                  </select></div>
              </div>
              <div><label className="text-xs font-medium text-foreground/60 mb-2 block">GST rate (%)</label>
                <input type="number" data-testid="inv-tax-rate" className={inputCls} value={form.tax_rate} onChange={(e) => set("tax_rate", e.target.value)} /></div>
              <div className="border-t border-border pt-4 space-y-2 text-sm">
                <div className="flex justify-between text-foreground/60"><span>Subtotal</span><span data-testid="inv-subtotal">{inr(t.subtotal)}</span></div>
                <div className="flex justify-between text-foreground/60"><span>Discount</span><span>−{inr(t.discount)}</span></div>
                <div className="flex justify-between text-foreground/60"><span>GST ({form.tax_rate || 0}%)</span><span>{inr(t.tax)}</span></div>
                <div className="flex justify-between font-semibold text-base pt-1"><span>Total</span><span data-testid="inv-total">{inr(t.total)}</span></div>
              </div>
              <button data-testid="save-invoice" onClick={save} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-white py-3 text-sm font-medium hover:bg-[#0d5f57] transition-colors disabled:opacity-60">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {editingId ? "Update invoice" : "Create invoice"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Reports ----------
  if (view === "report") {
    const s = reportData?.summary;
    return (
      <div data-testid="reports-view">
        <button onClick={() => setView("list")} className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-primary mb-5"><ArrowLeft size={15} /> Back to invoices</button>
        <div className="flex items-center gap-3 mb-6">
          <span className="grid place-items-center h-11 w-11 rounded-2xl bg-primary/10 text-primary"><BarChart3 size={20} /></span>
          <div><h2 className="font-heading text-2xl font-medium">Reports</h2><p className="text-sm text-foreground/55">Generate a revenue &amp; invoice report for any period.</p></div>
        </div>
        <div className="bg-card border border-border rounded-3xl p-6 mb-6 flex flex-wrap items-end gap-4">
          <div><label className="text-xs font-medium text-foreground/60 mb-2 block">From</label>
            <input type="date" data-testid="report-start" className={inputCls} value={reportFilters.start} onChange={(e) => setReportFilters({ ...reportFilters, start: e.target.value })} /></div>
          <div><label className="text-xs font-medium text-foreground/60 mb-2 block">To</label>
            <input type="date" data-testid="report-end" className={inputCls} value={reportFilters.end} onChange={(e) => setReportFilters({ ...reportFilters, end: e.target.value })} /></div>
          <div><label className="text-xs font-medium text-foreground/60 mb-2 block">Status</label>
            <select data-testid="report-status" className={inputCls} value={reportFilters.status} onChange={(e) => setReportFilters({ ...reportFilters, status: e.target.value })}>
              {["all", ...STATUSES].map((s2) => <option key={s2} value={s2}>{s2 === "all" ? "All" : s2}</option>)}
            </select></div>
          <button data-testid="generate-report" onClick={generateReport} disabled={genReport} className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-6 py-2.5 text-sm font-medium hover:bg-[#0d5f57] transition-colors disabled:opacity-60">
            {genReport ? <Loader2 size={15} className="animate-spin" /> : <BarChart3 size={15} />} Generate
          </button>
        </div>

        {s && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6" data-testid="report-summary">
              {[["Invoices", s.count, true], ["Total billed", s.total], ["Paid", s.paid], ["Outstanding", s.outstanding], ["GST collected", s.tax]].map(([l, v, isCount]) => (
                <div key={l} className="bg-card border border-border rounded-2xl p-4">
                  <div className="text-xs text-foreground/50 mb-1">{l}</div>
                  <div className="font-heading text-xl font-semibold text-foreground">{isCount ? v : inr(v)}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mb-4">
              <button data-testid="report-download-csv" onClick={downloadReportCSV} className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium text-foreground/70 hover:text-primary hover:border-primary transition-colors"><Download size={15} /> Download CSV</button>
              <button data-testid="report-print" onClick={printReport} className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium text-foreground/70 hover:text-primary hover:border-primary transition-colors"><Printer size={15} /> Print / PDF</button>
            </div>
            <div className="bg-card border border-border rounded-3xl overflow-hidden">
              {reportData.invoices.length === 0 ? (
                <div className="py-16 text-center text-foreground/50">No invoices in this period.</div>
              ) : (
                <div className="divide-y divide-border">
                  {reportData.invoices.map((inv) => (
                    <div key={inv.id} className="p-4 flex items-center gap-3 text-sm">
                      <span className="w-36 font-medium">{inv.number}</span>
                      <span className="flex-1">{inv.client_name}</span>
                      <span className="text-foreground/50 w-28">{inv.issue_date}</span>
                      <span className={`rounded-full px-3 py-1 text-xs font-medium ${statusStyle(inv.status)}`}>{inv.status}</span>
                      <span className="w-28 text-right font-semibold">{inr(inv.total)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  // ---------- List ----------
  return (
    <div data-testid="invoices-tab">
      <div className="flex items-center gap-3 mb-6">
        <span className="grid place-items-center h-11 w-11 rounded-2xl bg-primary/10 text-primary"><FileText size={20} /></span>
        <div><h2 className="font-heading text-2xl font-medium">Invoices</h2><p className="text-sm text-foreground/55">Create, track and send client invoices.</p></div>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="reports-btn" onClick={() => setView("report")} className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2.5 text-sm font-medium text-foreground/70 hover:text-primary hover:border-primary transition-colors"><BarChart3 size={15} /> Reports</button>
          <button data-testid="edit-billing-btn" onClick={() => setView("billing")} className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2.5 text-sm font-medium text-foreground/70 hover:text-primary hover:border-primary transition-colors"><Building2 size={15} /> Billing profile</button>
          <button data-testid="new-invoice-btn" onClick={startNew} className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-5 py-2.5 text-sm font-medium hover:bg-[#0d5f57] transition-colors"><Plus size={15} /> New invoice</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {["all", ...STATUSES].map((s) => (
          <button key={s} data-testid={`inv-filter-${s}`} onClick={() => setStatusFilter(s)}
            className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${statusFilter === s ? "bg-primary text-white" : "bg-white border border-border text-foreground/60 hover:text-primary"}`}>
            {s === "all" ? "All" : s}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-3xl overflow-hidden">
        {rows.length === 0 ? (
          <div className="py-20 text-center text-foreground/50"><FileText className="mx-auto mb-3" size={28} />No invoices yet. Create your first one.</div>
        ) : (
          <div className="divide-y divide-border">
            {rows.map((inv) => (
              <div key={inv.id} data-testid={`invoice-row-${inv.id}`} className="p-5 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="sm:w-40">
                  <div className="font-heading font-medium">{inv.number}</div>
                  <div className="text-xs text-foreground/45">{inv.issue_date || "—"}</div>
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm">{inv.client_name}</div>
                  <div className="text-xs text-foreground/50">{inv.client_company || inv.client_email || ""}</div>
                </div>
                <div className="text-right sm:w-32 font-semibold">{inr(inv.total)}</div>
                <select data-testid={`inv-row-status-${inv.id}`} value={inv.status || "Draft"} onChange={(e) => changeStatus(inv, e.target.value)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium border-0 outline-none cursor-pointer ${statusStyle(inv.status || "Draft")}`}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="flex items-center gap-1">
                  <button title="Print / PDF" data-testid={`inv-print-${inv.id}`} onClick={() => printInvoice(inv)} className="grid place-items-center h-9 w-9 rounded-xl hover:bg-primary/10 text-foreground/60 hover:text-primary transition-colors"><Printer size={16} /></button>
                  <button title="Email to client" data-testid={`inv-email-${inv.id}`} onClick={() => emailInvoice(inv)} className="grid place-items-center h-9 w-9 rounded-xl hover:bg-primary/10 text-foreground/60 hover:text-primary transition-colors"><Send size={16} /></button>
                  <button title="Edit" data-testid={`inv-edit-${inv.id}`} onClick={() => startEdit(inv)} className="grid place-items-center h-9 w-9 rounded-xl hover:bg-primary/10 text-foreground/60 hover:text-primary transition-colors"><Pencil size={16} /></button>
                  <button title="Delete" data-testid={`inv-delete-${inv.id}`} onClick={() => remove(inv)} className="grid place-items-center h-9 w-9 rounded-xl hover:bg-red-50 text-foreground/60 hover:text-red-500 transition-colors"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
